/**
 * Engine service: sticky analyze on position change → store.engine.score.
 * Best UCI is stored internally for analyze skip-logic — never shown in the default UI.
 */
import type { Color } from '@domain/chess/types';
import { gameStore, setGameStore } from '@state/gameStore';
import { createStockfishBridge } from './stockfishBridge';
import type { EngineBridge } from './bridge';

let bridge: EngineBridge | null = null;
let unsub: (() => void) | null = null;
let started = false;
let seq = 0;
let lastFen = '';

function sideToMove(fen: string): Color {
  const parts = fen.split(/\s+/);
  return parts[1] === 'b' ? 'b' : 'w';
}

/** Board, side, castling, ep — ignore clocks so Reveal of the same line is not a new job. */
function fenCore(fen: string): string {
  return fen.trim().replace(/\s+/g, ' ').split(' ').slice(0, 4).join(' ');
}

/** UCI scores are from side-to-move; we store white-positive. */
function toWhitePositive(
  score: { cp?: number; mate?: number },
  turn: Color,
): { cp?: number; mate?: number } {
  if (turn === 'w') return score;
  return {
    cp: score.cp !== undefined ? -score.cp : undefined,
    mate: score.mate !== undefined ? -score.mate : undefined,
  };
}

function ensureBridge(): EngineBridge {
  if (!bridge) {
    bridge = createStockfishBridge();
    unsub = bridge.subscribe((ev) => {
      if (ev.type === 'error') {
        setGameStore('engine', {
          ...gameStore.engine,
          status: 'error',
          errorMessage: ev.message,
        });
        return;
      }
      if (ev.type === 'ready') {
        setGameStore('engine', 'status', 'ready');
        return;
      }
      if (ev.type === 'info') {
        if (ev.requestId !== gameStore.engine.currentRequestId) return;
        const turn = sideToMove(gameStore.fen);
        const score = toWhitePositive(
          { cp: ev.cp, mate: ev.mate },
          turn,
        );
        setGameStore('engine', {
          ...gameStore.engine,
          status: 'thinking',
          score,
          depth: ev.depth,
          bestMoveUci: ev.pvUci ?? gameStore.engine.bestMoveUci,
        });
        return;
      }
      if (ev.type === 'bestmove') {
        if (ev.requestId !== gameStore.engine.currentRequestId) return;
        setGameStore('engine', {
          ...gameStore.engine,
          status: 'ready',
          bestMoveUci: ev.uci || gameStore.engine.bestMoveUci,
        });
      }
    });
  }
  return bridge;
}

/** Terminal mate: bar goes 100% to the winner; do not ask the engine. */
export function onCheckmate(winner: Color): void {
  lastFen = fenCore(gameStore.fen);
  setGameStore('engine', {
    ...gameStore.engine,
    score: { mate: winner === 'w' ? 1 : -1 },
    status: gameStore.engine.status === 'idle' ? 'idle' : 'ready',
    bestMoveUci: undefined,
    currentRequestId: null,
  });
}

/** Call when board fen changes (moves, undo, load FEN). */
export function onPositionChanged(fen: string): void {
  if (!started) return;
  const core = fenCore(fen);
  // Same position with a score already — keep it (Reveal of a solved line).
  if (core === lastFen && gameStore.engine.score) {
    return;
  }
  lastFen = core;

  const requestId = `r${++seq}`;
  const b = ensureBridge();
  setGameStore('engine', {
    ...gameStore.engine,
    currentRequestId: requestId,
    status: gameStore.engine.status === 'idle' ? 'loading' : 'thinking',
    stickyAnalyze: true,
    bestMoveUci: undefined,
  });

  void b.init().then(() => {
    if (gameStore.engine.currentRequestId !== requestId) return;
    b.analyze({ requestId, fen, depth: 14 });
  }).catch(() => {
    setGameStore('engine', 'status', 'error');
  });
}

/** Start sticky engine analysis (lazy). */
export function startEngineService(): void {
  if (started) return;
  started = true;
  setGameStore('engine', 'status', 'loading');
  const b = ensureBridge();
  void b.init().then(() => {
    setGameStore('engine', 'status', 'ready');
    onPositionChanged(gameStore.fen);
  }).catch(() => {
    setGameStore('engine', {
      ...gameStore.engine,
      status: 'error',
      errorMessage: 'Engine failed to load',
    });
  });
}

export function stopEngineService(): void {
  bridge?.stop();
  bridge?.dispose();
  bridge = null;
  unsub?.();
  unsub = null;
  started = false;
  lastFen = '';
}
